import { DataGrid } from "@mui/x-data-grid";
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./RecomendedProducts.css";
import BASE_URL from "../../../config/apiConfig";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
} from "@mui/material";

function RecommendedProducts() {
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [productToView, setProductToView] = useState(null);
  const [productToEdit, setProductToEdit] = useState(null);
  const [newProduct, setNewProduct] = useState({
    cropName: "",
    diseaseName: "",
    productName: "",
    productDescription: "",
    productLink: "",
    productImage: null, // New field to store image
  });

  // Fetch product data from API on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/admin/products`);
        const transformedData = response.data.map((product) => ({
          id: product.id,
          cropName: product.crop_name,
          diseaseName: product.disease_name,
          productName: product.product_name,
          productDescription: product.product_description,
          productLink: product.product_link,
          productImage: product.product_image, // Include product image URL
          createdAt: new Date(product.created_at).toLocaleDateString(),
        }));
        setData(transformedData);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchData();
  }, []);

  const handleDlt = (product) => {
    setProductToDelete(product);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/products/delete/${productToDelete.id}`
      );
      setData((prevData) =>
        prevData.filter((item) => item.id !== productToDelete.id)
      );

      setIsDeleteConfirmOpen(false);
      setProductToDelete(null);
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const handleView = (product) => {
    setProductToView(product);
    setIsViewOpen(true);
  };

  const handleEdit = (product) => {
    setProductToEdit({ ...product }); //this will make a copy of the product object to avoid changing the orginal object
    setIsEditOpen(true);
  };

  const handleInputChange = (e) => {
    if (e.target.name === "productImage") {
      const file = e.target.files[0];

      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/jpg",
      ];
      const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

      if (file && file.size <= maxSizeInBytes) {
        const reader = new FileReader();
        reader.onload = () => {
          console.log("Preview Image:", reader.result);
        };
        reader.readAsDataURL(file);

        if (newProduct) {
          setNewProduct({ ...newProduct, productImage: file });
        } else if (productToEdit) {
          setProductToEdit({ ...productToEdit, productImage: file }); // ✅ Ensure update state
        }
      } else {
        alert("Please upload a valid image file under 5MB.");
      }
    }
    else if (e.target.name === "updateProductImage") {
      const file = e.target.files[0];
      const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

      if (file && file.size <= maxSizeInBytes) {
        const reader = new FileReader();
        reader.onload = () => {
          console.log("Preview Image:", reader.result);
        };
        reader.readAsDataURL(file);

        if (productToEdit) {
          setProductToEdit({ ...productToEdit, productImage: file });
        }
      }
      else {
        alert("Please upload a valid image file under 5MB.");
      }
    }
     else {
      if (newProduct) {
        setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
      } else if (productToEdit) {
        setProductToEdit({ ...productToEdit, [e.target.name]: e.target.value });
      }
    }
  };

  const handleAddNew = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("crop_name", newProduct.cropName); // Updated key
      formData.append("disease_name", newProduct.diseaseName); // Updated key
      formData.append("product_name", newProduct.productName); // Updated key
      formData.append("product_description", newProduct.productDescription); // Updated key
      formData.append("product_link", newProduct.productLink); // Updated key
      if (newProduct.productImage) {
        formData.append("product_image", newProduct.productImage);
      }
      const response = await axios.post(
        `${BASE_URL}/api/admin/products/add`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const addedProduct = {
        id: response.data.id,
        cropName: response.data.crop_name,
        diseaseName: response.data.disease_name,
        productName: response.data.product_name,
        productDescription: response.data.product_description,
        productLink: response.data.product_link,
        productImage: response.data.product_image, // Ensure image is returned from API
        createdAt: new Date().toLocaleDateString(),
      };

      setData((prevData) => [...prevData, addedProduct]);
      setIsModalOpen(false);
      setNewProduct({
        cropName: "",
        diseaseName: "",
        productName: "",
        productDescription: "",
        productLink: "",
        productImage: null,
      });
    } catch (error) {
      console.error("Error adding new product:", error);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("crop_name", productToEdit.cropName);
      formData.append("disease_name", productToEdit.diseaseName);
      formData.append("product_name", productToEdit.productName);
      formData.append("product_description", productToEdit.productDescription);
      formData.append("product_link", productToEdit.productLink);

      if (
        productToEdit.productImage &&
        productToEdit.productImage instanceof File
      ) {
        console.log("🟢 Attaching Image:", productToEdit.productImage);
        formData.append("product_image", productToEdit.productImage);
      } else {
        console.warn("⚠️ No new image provided, keeping the old one.");
      }

      // Log formData before sending
      for (let pair of formData.entries()) {
        console.log(`📝 ${pair[0]}:`, pair[1]);
      }

      const response = await axios.post(
        `${BASE_URL}/api/admin/products/update/${productToEdit.id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const updatedProduct = {
        id: response.data.id,
        cropName: response.data.crop_name,
        diseaseName: response.data.disease_name,
        productName: response.data.product_name,
        productDescription: response.data.product_description,
        productLink: response.data.product_link,
        productImage: response.data.product_image, // Ensure image is returned from API
        createdAt: productToEdit.createdAt,
      };

      setData((prevData) =>
        prevData.map((item) =>
          item.id === updatedProduct.id ? updatedProduct : item
        )
      );
      setIsEditOpen(false);
      setProductToEdit(null);
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const columns = [
    // { field: "id", headerName: "ID", width: 200 },
    { field: "cropName", headerName: "Crop Name", width: 180 },
    { field: "diseaseName", headerName: "Disease Name", width: 180 },
    { field: "productName", headerName: "Product Name", width: 200 },
    {
      field: "productDescription",
      headerName: "Product Description",
      width: 200,
    },
    {
      field: "productLink",
      headerName: "Product Link",
      width: 150,
      renderCell: (params) => (
        <a
          href={params.row.productLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "blue", textDecoration: "underline" }}
        >
          {params.row.productLink}
        </a>
      ),
    },
    {
      field: "productImage",
      headerName: "Product Image",
      width: 150,
      renderCell: (params) => (
        <img
          src={`${BASE_URL}/${params.row.productImage}`}
          alt={params.row.ProductName}
          style={{ width: "100%", height: "auto" }}
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 300,
      renderCell: (params) => (
        <div className="action-buttons">
          <button
            type="button"
            className="view-button"
            onClick={() => handleView(params.row)}
          >
            View
          </button>
          <button
            type="button"
            className="edit-button"
            onClick={() => handleEdit(params.row)}
          >
            Edit
          </button>
          <button
            type="button"
            className="delete-button"
            onClick={() => handleDlt(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="btnn">
        <button type="button" onClick={() => setIsModalOpen(true)}>
          Add New Product
        </button>
      </div>

      <div className="data-table">
        <DataGrid
          className="data-grid"
          rows={data}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          checkboxSelection
          getRowId={(row) => row.id}
        />
      </div>

      {/* MUI Dialog for Add New Product */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>Add New Product</DialogTitle>
        <form onSubmit={handleAddNew}>
          <DialogContent>
            <TextField
              margin="dense"
              label="Crop Name"
              type="text"
              fullWidth
              name="cropName"
              value={newProduct.cropName}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              label="Disease Name"
              type="text"
              fullWidth
              name="diseaseName"
              value={newProduct.diseaseName}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              label="Product Name"
              type="text"
              fullWidth
              name="productName"
              value={newProduct.productName}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              label="Product Description"
              type="text"
              fullWidth
              name="productDescription"
              value={newProduct.productDescription}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              label="Product Link"
              type="url"
              fullWidth
              name="productLink"
              value={newProduct.productLink}
              onChange={handleInputChange}
              required
            />
            {/* Image Upload */}
            <input
              type="file"
              name="productImage"
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="submit">Save</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* MUI Dialog for Edit Product */}
      <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)}>
        <DialogTitle>Edit Product</DialogTitle>
        <form onSubmit={handleUpdateProduct}>
          <DialogContent>
            <TextField
              margin="dense"
              label="Crop Name"
              type="text"
              fullWidth
              name="cropName"
              value={productToEdit?.cropName || ""}
              onChange={(e) =>
                setProductToEdit({ ...productToEdit, cropName: e.target.value })
              }
              required
            />
            <TextField
              margin="dense"
              label="Disease Name"
              type="text"
              fullWidth
              name="diseaseName"
              value={productToEdit?.diseaseName || ""}
              onChange={(e) =>
                setProductToEdit({
                  ...productToEdit,
                  diseaseName: e.target.value,
                })
              }
              required
            />
            <TextField
              margin="dense"
              label="Product Name"
              type="text"
              fullWidth
              name="productName"
              value={productToEdit?.productName || ""}
              onChange={(e) =>
                setProductToEdit({
                  ...productToEdit,
                  productName: e.target.value,
                })
              }
              required
            />
            <TextField
              margin="dense"
              label="Product Description"
              type="text"
              fullWidth
              name="productDescription"
              value={productToEdit?.productDescription || ""}
              onChange={(e) =>
                setProductToEdit({
                  ...productToEdit,
                  productDescription: e.target.value,
                })
              }
              required
            />
            <TextField
              margin="dense"
              label="Product Link"
              type="url"
              fullWidth
              name="productLink"
              value={productToEdit?.productLink || ""}
              onChange={(e) =>
                setProductToEdit({
                  ...productToEdit,
                  productLink: e.target.value,
                })
              }
              required
            />
            {/* Image Upload */}
            <input
              type="file"
              name="updateProductImage"
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button type="submit">Save</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* MUI Dialog for View Product */}
      <Dialog open={isViewOpen} onClose={() => setIsViewOpen(false)}>
        <DialogTitle>View Product</DialogTitle>
        <DialogContent>
          {productToView && (
            <>
              <p>Crop Name: {productToView.cropName}</p>
              <p>Disease Name: {productToView.diseaseName}</p>
              <p>Product Name: {productToView.productName}</p>
              <p>Product Description: {productToView.productDescription}</p>
              <p>Product Link: {productToView.productLink}</p>
              <img
                src={`${productToView.productImage}`}
                alt="Product"
                style={{ width: 200, height: 200, objectFit: "cover" }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RecommendedProducts;
